import React, { useMemo } from 'react'
import Select from 'react-select'
import { SignatureAlerte } from '../Types/Class/SignatureAlerte'
import { Relance } from '../Types/Interface/Relance'
import { People } from '../../../../types/People'
import { UseAlertsManagementReturn } from '../hooks/useAlertsManagement'
import { UseRelancesManagementReturn } from '../hooks/useRelancesManagement'
import { SelectOption } from '../../../../types/SelectOption'

interface AlertsSectionProps {
    alertsHook: UseAlertsManagementReturn
    relancesHook: UseRelancesManagementReturn
    users: People[]
}

export default function AlertsSection({ alertsHook, relancesHook, users }: AlertsSectionProps) {
    const availableUserOptions: SelectOption[] = useMemo(() =>
        users
            .filter(u => !alertsHook.personnesAlerter.some(p => p.IDPeopleToAlert === u.IDPeople))
            .map(user => ({
                label: user.FullName || `${user.Lastname} ${user.Firstname.charAt(0).toUpperCase() + user.Firstname.slice(1).toLowerCase()}`,
                value: user.IDPeople
            }))
    , [users, alertsHook.personnesAlerter])

    const getSelectedUserOption = (): SelectOption | null => {
        if (!alertsHook.selectedPersonneAlerte) return null
        return availableUserOptions.find(opt => opt.value === alertsHook.selectedPersonneAlerte) || null
    }

    return (
        <div className="card mb-4">
            <div className="card-header">
                <h4>Gestion des alertes et des relances</h4>
            </div>
            <div className="card-body">
                <div className="row">
                    {/* Alerts Section */}
                    <div className="col-md-6">
                        <h5>Gestion des alertes</h5>

                        <div className="form-group">
                            <label>Seuil de crédits restants d'alerte :</label>
                            <input
                                type="number"
                                className="form-control"
                                value={alertsHook.seuilAlerte}
                                onChange={(e) => alertsHook.setSeuilAlerte(parseInt(e.target.value) || 0)}
                                placeholder="Seuil"
                            />
                        </div>

                        <div className="form-group">
                            <label>Personnes à alerter :</label>
                            <div className="d-flex mb-2">
                                <div style={{ flex: 1 }}>
                                    <Select
                                        options={availableUserOptions}
                                        value={getSelectedUserOption()}
                                        onChange={(option: SelectOption | null) => alertsHook.setSelectedPersonneAlerte(option ? Number(option.value) : null)}
                                        placeholder="Utilisateur"
                                        searchable={true}
                                        clearable={true}
                                    />
                                </div>
                                <button
                                    className="btn btn-success ml-2"
                                    onClick={alertsHook.handleAddPersonneAlerte}
                                    disabled={!alertsHook.selectedPersonneAlerte}
                                >
                                    Ajouter
                                </button>
                            </div>

                            {alertsHook.personnesAlerter.length > 0 && (
                                <div className="mt-2">
                                    {alertsHook.personnesAlerter.map((alerte, index) => {
                                        const user = users.find(u => u.IDPeople === alerte.IDPeopleToAlert)
                                        return (
                                            <span key={alerte.IDPeopleToAlert || index} className="badge badge-primary mr-2 mb-2" style={{ fontSize: '14px', padding: '8px' }}>
                                                {user?.FullName}
                                                <button
                                                    type="button"
                                                    className="close ml-2"
                                                    onClick={() => alertsHook.handleRemovePersonneAlerte(alerte.IDPeopleToAlert!)}
                                                    style={{ fontSize: '18px' }}
                                                >
                                                    &times;
                                                </button>
                                            </span>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Reminders Section */}
                    <div className="col-md-6">
                        <h5>Gestion des relances</h5>

                        <div className="form-group">
                            <label>Ajouter une relance pour la demande de signature :</label>
                            <div className="row">
                                <div className="col-6">
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Valeur"
                                        min="1"
                                        value={relancesHook.newRelance.value}
                                        onChange={(e) => relancesHook.setNewRelance({ ...relancesHook.newRelance, value: parseInt(e.target.value) || 1 })}
                                    />
                                </div>
                                <div className="col-4">
                                    <select
                                        className="form-control"
                                        value={relancesHook.newRelance.unit}
                                        onChange={(e) => relancesHook.setNewRelance({ ...relancesHook.newRelance, unit: e.target.value as 'd' | 'w' | 'm' })}
                                    >
                                        <option value="d">jour</option>
                                        <option value="w">semaine</option>
                                        <option value="m">mois</option>
                                    </select>
                                </div>
                                <div className="col-2">
                                    <button
                                        className="btn btn-success"
                                        onClick={relancesHook.handleAddRelance}
                                    >
                                        Ajouter
                                    </button>
                                </div>
                            </div>
                        </div>

                        {relancesHook.relances.length > 0 && (
                            <table className="table table-sm table-bordered">
                                <thead className="thead-light">
                                    <tr>
                                        <th>Valeur</th>
                                        <th>Unité</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {relancesHook.relances.map((relance, index) => (
                                        <tr key={index}>
                                            <td>{relance.value}</td>
                                            <td>{relancesHook.getRelanceLabel(relance)}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-danger"
                                                    onClick={() => relancesHook.handleDeleteRelance(index)}
                                                >
                                                    Supprimer
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}
